import React, { useCallback,  } from "react";
import { Col, Form, Row, Upload } from "antd";
import { useDispatch } from "react-redux";

import { TextField } from "@/components";
import { superAppActions } from "@/redux/actions";
import { useNotification } from "@/hooks";

import { InboxOutlined } from "@ant-design/icons";
import { useEffect } from "react";

const DraggerField = ({
    label,
    fieldName,
    required,
    onSuccessUpload,
    dependencies,
    objectId,
    platform,
}) => {
    const dispatch = useDispatch();
    const form = Form.useFormInstance();

    const draggerProps = {
        name: "file",
        maxCount: 1,
        customRequest: ({ file, onSuccess, onError }) => {
            dispatch(
                superAppActions.uploadBundle({
                    params: {
                        file,
                        platform,
                        id: objectId,
                    },
                    onCompleted: (result) => {
                        if (result.result) {
                            onSuccess(result?.data);
                        } else {
                            onError();
                        }
                        onSuccessUpload?.(result);
                    },
                    onError,
                })
            );
        },
    };

    const DraggerInput = useCallback(({ value, ...inputProps }) => {
        const { status } = Form.Item.useStatus();
        return (
            <Upload.Dragger
                {...draggerProps}
                {...inputProps}
                style={status === "error" ? { border: "1px solid red" } : {}}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                    Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint">
                    Support for a single or bulk upload. Strictly prohibited
                    from uploading company data or other banned files.
                </p>
            </Upload.Dragger>
        );
    }, []);

    useEffect(() => {
        if (required) return;

        form.validateFields([fieldName]);
    }, [required]);

    return (
        <Form.Item
            name={fieldName}
            label={label}
            rules={[
                {
                    required,
                    message: "Vui lòng chọn file",
                },
            ]}
            dependencies={dependencies}
        >
            <DraggerInput />
        </Form.Item>
    );
};

const VersionForm = ({ form, formId, detailData, isCreating, onSubmit }) => {
    const { showErrorMessage, showSuccessMessage } = useNotification();
    const handleSubmit = (values) => {
        onSubmit(values);
    };

    const onSuccessUpload = (
        res,
        fieldName,
        secondFieldName,
        thirdFieldName
    ) => {
        if (res.result) {
            showSuccessMessage(res.message);
            form.setFieldValue(fieldName, res.data.avatarUrl);
            form.setFieldValue(secondFieldName, res.data.sizeFile);
            form.setFieldValue(thirdFieldName, res.data.contentMd5);
        } else {
            showErrorMessage(res.message);
        }
    };

    const regex = /(^\.|\.$|[^\d.]+)/g;
    const bundleIOSUrl = Form.useWatch(["bundleIOSUrl"], form);
    const bundleAndroidUrl = Form.useWatch(["bundleAndroidUrl"], form);

    return (
        <Form
            id={formId}
            onFinish={handleSubmit}
            form={form}
            layout="vertical"
            initialValues={detailData}
        >
            <Row gutter={16}>
                <Col span={24}>
                    <TextField
                        fieldName="appVersion"
                        label="Phiên bản"
                        validators={[
                            {
                                validator: (_, value) => {
                                    if (value && regex.test(value)) {
                                        return Promise.reject(
                                            new Error(
                                                "Phiên bản chỉ được phép nhập số và . nhưng . không được ở đầu và cuối!"
                                            )
                                        );
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        fieldName="appVersionIos"
                        label="Phiên bản IOS"
                        validators={[
                            {
                                validator: (_, value) => {
                                    if (value && regex.test(value)) {
                                        return Promise.reject(
                                            new Error(
                                                "Phiên bản chỉ được phép nhập số và . nhưng . không được ở đầu và cuối!"
                                            )
                                        );
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        fieldName="appVersionAndroid"
                        label="Phiên bản Android"
                        validators={[
                            {
                                validator: (_, value) => {
                                    if (value && regex.test(value)) {
                                        return Promise.reject(
                                            new Error(
                                                "Phiên bản chỉ được phép nhập số và . nhưng . không được ở đầu và cuối!"
                                            )
                                        );
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        fieldName="bundleNameIos"
                        label="Tên bundle IOS"
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        fieldName="bundleNameAndroid"
                        label="Tên bundle Android"
                    />
                </Col>
                <Col span={12}>
                    <DraggerField
                        objectId={detailData.id}
                        label="Bundle"
                        fieldName="bundleIos"
                        required={!bundleIOSUrl && !bundleAndroidUrl}
                        onSuccessUpload={(res) =>
                            onSuccessUpload(
                                res,
                                "bundleIOSUrl",
                                "sizePackageIos",
                                "md5Ios"
                            )
                        }
                        platform="IOS"
                        dependencies={["bundleAndroid"]}
                    />
                </Col>
                <Col span={12}>
                    <DraggerField
                        objectId={detailData.id}
                        label="Bundle"
                        fieldName="bundleAndroid"
                        required={!bundleIOSUrl && !bundleAndroidUrl}
                        onSuccessUpload={(res) =>
                            onSuccessUpload(
                                res,
                                "bundleAndroidUrl",
                                "sizePackageAndroid",
                                "md5Android"
                            )
                        }
                        platform={"ANDROID"}
                        dependencies={["bundleIos"]}
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        fieldName="bundleIOSUrl"
                        label="Bundle URL IOS"
                        disabled
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        fieldName="bundleAndroidUrl"
                        label="Bundle URL Android"
                        disabled
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        fieldName="sizePackageIos"
                        label="Dung lượng file bundle IOS"
                        disabled
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        fieldName="sizePackageAndroid"
                        label="Dung lượng file bundle Android"
                        disabled
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        fieldName="md5Ios"
                        label="Md5 checksum IOS"
                        disabled
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        fieldName="md5Android"
                        label="Md5 checksum Android"
                        disabled
                    />
                </Col>
            </Row>
        </Form>
    );
};

export default VersionForm;
